import System from "../System";
import AudioClip, { AudioTrackType } from "../Audio/AudioClip";
import { IsMobile } from "Utility/Platform";
import { IGameState } from "State/Game/IGameState";
import { StopMusic, getAudioMusic, getAudioSFX } from "./AudioHelper";
import { Howler } from 'howler';
import Game from "Game";

export enum AudioSystemEvent {
    PLAY = 'play_audio',
    STOP = 'stop_audio',
    VOLUME = 'set_volume',
}

// WARNING: these values are also used as storage key
// Warning: values of this enum are also used as localization keys
// TODO: Replace enum usage and/or string-building for asset references.
export enum AudioStreamType {
    MUSIC = 'audioMusic',
    SFX = 'audioSfx',
}

export default class AudioSystem extends System {
    public audioClips: AudioClip[];

    public audioEnabled: boolean = true;
    public windowHasFocus: boolean = true;
    public SFXVolume: number = 0;
    public MusicVolume: number = 0;
    public currentMusic: AudioClip;

    protected isFrozenByAd: boolean = false;

    private findClipFromString(_clip: string): AudioClip | null {
        if (!_clip) return null;
        return this.audioClips.find(
            (e: AudioClip) => e.name === _clip
        ) || null;
    }

    public freezeGame(): void {
        console.log("FREEZING GAME!!!")
       // Howler.mute(true);
        Howler.mute(true);
        this.isFrozenByAd = true;
    }

    public unfreezeGame(): void {
        const currentVolume = Game.store.getState().playerState.settings[AudioStreamType.MUSIC];
        const wasMuted = currentVolume === 0;
        Howler.mute(document.hidden || wasMuted);
        this.isFrozenByAd = false;
    }

    public init() {
        this.audioClips = [];

        //@ts-ignore
        FBInstant.setPauseHandler(() => {
           this.freezeGame();
       });

       //@ts-ignore
       FBInstant.setUnpauseHandler(() => {
           this.unfreezeGame();
       });

        this.scene.on(AudioSystemEvent.PLAY, (_clip: AudioClip) => {
            // Prevent playing multiple instances of a clip if `allowMultipleInstances` is `true`.
            if (this.findClipFromString(_clip.name) && !_clip.allowMultipleInstances) return;

            this.playAudio(_clip);
        });
        this.scene.on(AudioSystemEvent.STOP, (_clip: AudioClip | string) => {
            if (_clip instanceof AudioClip) {
                this.destroyAudioClip(_clip);
            } else {
                const clip: AudioClip = this.findClipFromString(_clip);
                if (clip) {
                    this.destroyAudioClip(clip);
                } else {
                    console.warn(`Failed to find clip to stop: "${_clip}"`);
                }
            }
        });
        this.scene.on(AudioSystemEvent.VOLUME, (_clip: AudioClip | string, _volume: number) => {
            if (_clip instanceof AudioClip) {
                _clip.volume = _volume;
            } else {
                const clip: AudioClip = this.findClipFromString(_clip);
                if (clip) {
                    clip.volume = _volume;
                } else {
                    console.warn(`Failed to find clip to change volume: "${_clip}"`);
                }
            }
        });

        if (window) {
            window.addEventListener('pagehide', this.checkFocus.bind(this));
            window.addEventListener('pageshow', this.checkFocus.bind(this));
            window.addEventListener('focus', this.checkFocus.bind(this));
            window.addEventListener('blur', this.checkFocus.bind(this));

            window.onblur = this.checkFocus.bind(this);
            window.onfocus = this.checkFocus.bind(this);
        }
        document.addEventListener('pagehide', this.checkFocus.bind(this));
        document.addEventListener('pageshow', this.checkFocus.bind(this));
        document.addEventListener('focus', this.checkFocus.bind(this));
        document.addEventListener('blur', this.checkFocus.bind(this));
        document.addEventListener(
            'visibilitychange',
            this.checkFocus.bind(this)
        );

        document.onfocus = this.checkFocus.bind(this);
        document.onblur = this.checkFocus.bind(this);

        this.checkFocus();
    }

    public handleStateChanged(state: IGameState): void {
        if (state.playerState.settings === null) return;
        this.audioEnabled = state.context.audioEnabled;
        this.SFXVolume = Number(getAudioSFX(state));
        this.MusicVolume = Number(getAudioMusic(state));

        this.handleAudio();
    }

    private isMuted() {
        return this.audioEnabled === false || this.windowHasFocus === false;
    }

    private handleAudio() {
        // @ts-ignore
        if (this.isFrozenByAd) return;
        
        Howler.mute(this.isMuted());

        this.audioClips.forEach(clip => {
            clip.volume = this.getClipVolume(clip);
        });
    }

    private getClipVolume(clip: AudioClip): number {
        switch (clip.track) {
            case AudioTrackType.SFX:
                return this.SFXVolume;
            case AudioTrackType.MUSIC:
                return this.MusicVolume;
        }

        return 0;
    }

    private checkFocus() {
        if (IsMobile()) {
            this.windowHasFocus = document.visibilityState === 'visible';
        } else {
            this.windowHasFocus =
                document.visibilityState === 'visible' && document.hasFocus();
        }

        this.handleAudio();
    }

    public onDestroy(): void {
        this.audioClips.slice(0).forEach(this.destroyAudioClip.bind(this));
        this.audioClips = [];

        this.scene.off(AudioSystemEvent.PLAY);
        this.scene.off(AudioSystemEvent.STOP);
    }

    public playAudio(clip: AudioClip) {
        // Add the clip to the overall audio clips list.
        this.audioClips.push(clip);

        // Set up the onComplete handler and volume.
        clip.onComplete(this.destroyAudioClip.bind(this));
        clip.volume = this.getClipVolume(clip);
        clip.play();

        // If this is a MUSIC track, handle fading out the currentMusic.
        if (clip.track === AudioTrackType.MUSIC) {
            if (this.currentMusic) {
                const oldMusic = this.currentMusic;
                // Check if the old music's Howl instance is loaded and if fade() is supported.
                if (oldMusic.howl) {
                    console.log(`[AudioSystem] Fading out old music.`);
                    oldMusic.howl.fade(oldMusic.volume, 0, 500);
                    // Use 'once' so that the callback is only run once.
                    oldMusic.howl.once('fade', () => {
                        StopMusic(oldMusic);
                    });
                } else {
                    // Fallback: stop immediately if fading isn't available.
                    console.log(`[AudioSystem] Old music not fully loaded or fade not available. Stopping immediately.`);
                    StopMusic(oldMusic);
                }

                // For the new music clip, try fading it in if possible.
                if (clip.howl) {
                    clip.howl.fade(0, clip.volume, 500);
                }
            }
            // Set the new clip as the current music.
            this.currentMusic = clip;
        }
    }

    private destroyAudioClip(clip: AudioClip) {
        const index = this.audioClips.indexOf(clip);
        if (index >= 0) this.audioClips.splice(index, 1);

        clip.destroy();
    }

    protected onLateUpdate(): void { }

    protected onStart(): void { }

    protected onStop(): void { }

    protected onUpdate(): void { }

    async preload(): Promise<any> {
        return undefined;
    }
}
